'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Services', href: '/services' },
      { label: 'Partner Network', href: '/partner-network' },
      { label: 'Resources', href: '/resources' },
    ],
    support: [
      { label: 'Contact', href: '/contact' },
      { label: 'FAQ', href: '/resources' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
    social: [
      { label: 'LinkedIn', icon: 'LinkIcon', href: '#' },
      { label: 'Twitter', icon: 'LinkIcon', href: '#' },
      { label: 'YouTube', icon: 'LinkIcon', href: '#' },
    ],
  };

  return (
    <footer className={`bg-primary text-white ${className}`}>
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="40" rx="8" fill="white" opacity="0.1" />
                <path
                  d="M20 10L12 18H16V28H24V18H28L20 10Z"
                  fill="white"
                  opacity="0.9"
                />
                <circle cx="20" cy="22" r="2" fill="white" />
              </svg>
              <div className="flex flex-col">
                <span className="text-lg font-bold font-headline leading-tight">
                  AI Change Leaders
                </span>
                <span className="text-xs text-white/70 font-body">
                  Human-Centered Transformation
                </span>
              </div>
            </div>
            <p className="text-sm text-white/80 font-body leading-relaxed mb-6">
              Bridging the gap between AI investment and organizational adoption through proven change management frameworks.
            </p>
            <div className="flex gap-4">
              {footerLinks.social.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-300"
                  aria-label={social.label}
                >
                  <Icon name={social.icon as any} size={20} variant="outline" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold font-headline mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/80 hover:text-white transition-colors duration-300 font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold font-headline mb-6">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/80 hover:text-white transition-colors duration-300 font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold font-headline mb-6">Get Started</h3>
            <p className="text-sm text-white/80 font-body mb-4">
              Ready to transform your AI journey? Book a discovery call today.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold font-cta text-sm text-primary bg-white hover:bg-white/90 transition-all duration-300"
            >
              Book Discovery Call
              <Icon name="ArrowRightIcon" size={16} variant="solid" />
            </Link>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/70 font-body">
              {isHydrated ? `© ${currentYear}` : '© 2026'} AI Change Leaders. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                href="/privacy"
                className="text-sm text-white/70 hover:text-white transition-colors duration-300 font-body"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="text-sm text-white/70 hover:text-white transition-colors duration-300 font-body"
              >
                Terms of Service
              </Link>
              <Link
                href="/admin-login-portal"
                className="text-xs text-white/40 hover:text-white/60 transition-colors duration-300 font-body"
                title="Admin Access"
              >
                •
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;